import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  url?: string;
  type?: 'website' | 'product' | 'article' | 'profile';
  profileUsername?: string;
  structuredData?: object;
  additionalSchemas?: object[];
}

const DEFAULT_TITLE = 'TicoMarket — Feria en Línea de Costa Rica';
const DEFAULT_DESCRIPTION = 'Compra y vende productos únicos de emprendedores ticos. Miles de tiendas locales, pagos seguros y soporte rápido en Costa Rica.';
const DEFAULT_KEYWORDS = 'feria en línea, marketplace Costa Rica, comprar online, vender online, emprendedores ticos, tiendas locales, SINPE móvil';
const BASE_URL = 'https://www.ticomarket.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_OG_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
  url,
  type = 'website',
  profileUsername,
  structuredData,
  additionalSchemas = [],
}: SEOProps) => {
  const location = useLocation();
  // Self-referencing canonical: fall back to the current route, not the homepage.
  const pathname = location.pathname === '/' ? '' : location.pathname.replace(/\/+$/, '');
  const canonicalUrl = (url ?? `${BASE_URL}${pathname}`).replace(
    /^https:\/\/ticomarket\.com/,
    BASE_URL,
  );
  const fullTitle = title ? `${title} | TicoMarket` : DEFAULT_TITLE;
  
  
  // Default Organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TicoMarket',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    description: DEFAULT_DESCRIPTION,
    sameAs: [
      'https://twitter.com/ticomarket'
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CR'
    }
  };

  // Website schema with search action
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TicoMarket',
    url: BASE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/productos?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="TicoMarket" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'profile' ? 'profile' : type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={title || 'TicoMarket'} />
      <meta property="og:locale" content="es_CR" />
      <meta property="og:site_name" content="TicoMarket" />

      {/* Profile-specific OG tags for stores */}
      {type === 'profile' && profileUsername && (
        <>
          <meta property="profile:username" content={profileUsername} />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title || 'TicoMarket'} />
      <meta name="twitter:site" content="@ticomarket" />
      <meta name="twitter:creator" content="@ticomarket" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      {additionalSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
